// src/components/pages/b2b/ESG/EsgStrategy.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

const MAIN_COLOR = "#005F91";
const ACCENT_COLOR = "#0090A0";
const MILESTONE_HOVER_COLOR = "#FFF2BE";

const ESG_STRATEGY_DATA = {
  sectionTitle: {
    ZH: "ESG 永續策略",
    EN: "ESG Sustainability Strategy",
    JP: "ESGサステナビリティ戦略",
  },

  subtitle: {
    ZH: "專業衛生管理・環境安全・產業共創",
    EN: "Professional Hygiene Management, Environmental Safety, and Industry Co-creation",
    JP: "専門衛生管理・環境安全・産業共創",
  },

  intro: {
    ZH: "台灣水禾以食品衛生安全為核心，透過 ESG 經營策略，持續強化企業體質、提升產業價值，並建立具長期競爭力的永續營運模式。以「專業衛生管理、環境安全、產業共創」為方向，推動全球化視野下的組織升級與營運優化。",
    EN: "With food hygiene and safety at its core, Taiwan SEHO continuously strengthens corporate capability, enhances industry value, and establishes a sustainable operating model with long-term competitiveness through ESG management strategies. Guided by the principles of professional hygiene management, environmental safety, and industry collaboration, we promote organizational transformation and operational optimization from a global perspective.",
    JP: "台湾水禾は食品衛生安全を核心として、ESG経営戦略を通じて企業体質を継続的に強化し、産業価値を高め、長期的な競争力を備えた持続可能な運営モデルを構築しています。『専門衛生管理・環境安全・産業共創』を方向性として、グローバルな視点のもとで組織改革と運営最適化を推進しています。",
  },

  coreSection: {
    title: {
      ZH: "核心發展方向",
      EN: "Core Development Directions",
      JP: "主要な発展方向",
    },

    slides: [
      {
        key: "global-operation",

        image: "/img/ESG/strategy01.webp",

        title: {
          ZH: "全球化營運與組織升級",
          EN: "Global Operations and Organizational Transformation",
          JP: "グローバル運営と組織改革",
        },

        bullets: {
          ZH: [
            "建立具國際視野的食品衛生與環境安全服務體系。",
            "強化全球化所需的人才培育、專業技術與組織協作能力。",
            "推動組織水平化與跨部門整合，提高決策與執行效率。",
          ],
          EN: [
            "Establish an internationally oriented food hygiene and environmental safety service system.",
            "Strengthen talent development, professional expertise, and organizational collaboration capabilities required for globalization.",
            "Promote organizational flattening and cross-department integration to improve decision-making and execution efficiency.",
          ],
          JP: [
            "国際的視野を備えた食品衛生および環境安全サービス体制を構築する。",
            "グローバル化に必要な人材育成、専門技術、および組織協力能力を強化する。",
            "組織のフラット化と部門横断的統合を推進し、意思決定と実行効率を向上させる。",
          ],
        },
      },

      {
        key: "operation-resilience",

        image: "/img/ESG/strategy02.webp",

        title: {
          ZH: "提升營運效率與企業韌性",
          EN: "Improving Operational Efficiency and Corporate Resilience",
          JP: "運営効率と企業レジリエンスの向上",
        },

        bullets: {
          ZH: [
            "持續優化資本效率、營運流程與獲利結構。",
            "以風險管理與數據化管理思維，提高企業長期穩定性。",
            "從全球市場與產業趨勢中，準確掌握未來風險與發展機會。",
          ],
          EN: [
            "Continuously optimize capital efficiency, operational processes, and profitability structure.",
            "Improve long-term corporate stability through risk management and data-driven management thinking.",
            "Accurately identify future risks and development opportunities from global market and industry trends.",
          ],
          JP: [
            "資本効率、運営プロセス、および収益構造を継続的に最適化する。",
            "リスク管理とデータ活用型管理の考え方により、企業の長期的安定性を高める。",
            "グローバル市場と産業動向から、将来のリスクと成長機会を的確に把握する。",
          ],
        },
      },

      {
        key: "partner-value",

        image: "/img/ESG/strategy03.webp",

        title: {
          ZH: "與合作夥伴共同創造價值",
          EN: "Creating Value Together with Partners",
          JP: "パートナーと共に価値を創造",
        },

        bullets: {
          ZH: [
            "與供應鏈、產業夥伴及客戶建立長期合作關係。",
            "提升價值鏈整體的食品安全、衛生管理與永續能力。",
            "強化合作夥伴之間的信任、共鳴與共同創造機制。",
          ],
          EN: [
            "Build long-term partnerships with supply chains, industry partners, and customers.",
            "Improve food safety, hygiene management, and sustainability capabilities across the value chain.",
            "Strengthen trust, shared understanding, and co-creation mechanisms among partners.",
          ],
          JP: [
            "サプライチェーン、産業パートナー、お客様との長期的な協力関係を構築する。",
            "バリューチェーン全体の食品安全、衛生管理、および持続可能性を向上させる。",
            "パートナー間の信頼、共感、共同創造の仕組みを強化する。",
          ],
        },
      },
    ],
  },
  futureSection: {
    title: {
      ZH: "未來展望與承諾",
      EN: "Future Vision and Commitments",
      JP: "将来展望とコミットメント",
    },

    description: {
      ZH: "台灣水禾將持續以「創新驅動、專業衛生、永續經營」為核心，\n提供更安全、高效且符合未來需求的食品衛生與環境管理方案。",
      EN: "Taiwan SEHO will continue to focus on innovation-driven development, professional hygiene management, and sustainable operations to provide safer, more efficient, and future-oriented food hygiene and environmental management solutions.",
      JP: "台湾水禾は今後も『革新主導』『専門衛生』『持続可能な経営』を核心として、より安全で効率的かつ未来のニーズに適合した食品衛生および環境管理ソリューションを提供していきます。",
    },

    focusTitle: {
      ZH: "未來重點方向",
      EN: "Future Key Directions",
      JP: "今後の重点方向",
    },

    focusItems: {
      ZH: [
        "持續導入綠色技術、智慧設備與數位化管理，提升營運效率並降低環境負擔。",
        "建立更高標準的食品衛生、安全管理與作業流程，降低營運風險。",
        "與供應鏈夥伴共同推動 ESG 管理，提升整體產業鏈的責任與透明度。",
        "持續強化 ESG 指標管理與資訊揭露，提升企業治理與外部信任。",
      ],
      EN: [
        "Continue introducing green technologies, smart equipment, and digital management systems to improve operational efficiency and reduce environmental impact.",
        "Establish higher standards for food hygiene, safety management, and operational procedures to reduce operational risks.",
        "Work together with supply chain partners to promote ESG management and improve responsibility and transparency across the industry chain.",
        "Continuously strengthen ESG indicator management and information disclosure to enhance corporate governance and external trust.",
      ],
      JP: [
        "グリーン技術、スマート設備、デジタル管理を継続的に導入し、運営効率を向上させるとともに環境負荷を低減する。",
        "より高い基準の食品衛生、安全管理、および作業プロセスを構築し、運営リスクを低減する。",
        "サプライチェーンパートナーと共同でESG管理を推進し、産業チェーン全体の責任と透明性を向上させる。",
        "ESG指標管理と情報開示を継続的に強化し、企業統治と外部からの信頼を高める。",
      ],
    },

    milestones: [
      {
        key: "short",

        title: {
          ZH: "短期 (1–2 年)",
          EN: "Short Term (1–2 Years)",
          JP: "短期 (1–2年)",
        },

        items: {
          ZH: [
            "完成 ESG 基礎制度建置",
            "導入基準查核與永續資訊揭露",
            "建立能源與營運數據管理機制",
          ],
          EN: [
            "Complete the foundation of ESG management systems",
            "Introduce standard audits and sustainability information disclosure",
            "Establish energy and operational data management mechanisms",
          ],
          JP: [
            "ESG基礎制度の構築を完了する",
            "基準監査とサステナビリティ情報開示を導入する",
            "エネルギーおよび運営データ管理体制を構築する",
          ],
        },
      },

      {
        key: "mid",

        title: {
          ZH: "中期 (3–5 年)",
          EN: "Mid Term (3–5 Years)",
          JP: "中期 (3–5年)",
        },

        items: {
          ZH: [
            "推動節能改善與設備優化",
            "強化食品衛生教育與標準推廣",
            "提升供應鏈管理與永續採購制度",
          ],
          EN: [
            "Promote energy-saving improvements and equipment optimization",
            "Strengthen food hygiene education and standard promotion",
            "Enhance supply chain management and sustainable procurement systems",
          ],
          JP: [
            "省エネルギー改善と設備最適化を推進する",
            "食品衛生教育と基準普及を強化する",
            "サプライチェーン管理と持続可能な調達制度を向上させる",
          ],
        },
      },

      {
        key: "long",

        title: {
          ZH: "長期 (6–10 年)",
          EN: "Long Term (6–10 Years)",
          JP: "長期 (6–10年)",
        },

        items: {
          ZH: [
            "提高循環材料與環保設備使用比例",
            "建立跨產業永續合作模式",
            "發展具國際競爭力的食品衛生與環境安全品牌",
          ],
          EN: [
            "Increase the use of circular materials and environmentally friendly equipment",
            "Establish cross-industry sustainability collaboration models",
            "Develop a globally competitive brand in food hygiene and environmental safety",
          ],
          JP: [
            "循環材料と環境配慮型設備の使用比率を高める",
            "産業横断型の持続可能な協力モデルを構築する",
            "国際競争力のある食品衛生・環境安全ブランドを発展させる",
          ],
        },
      },
    ],
  },

  governanceSection: {
    title: {
      ZH: "商業行為與治理原則",
      EN: "Business Conduct and Governance Principles",
      JP: "事業行動とガバナンス原則",
    },

    description: {
      ZH: "台灣水禾以誠信、專業與責任作為經營基礎，建立穩定且可持續的企業治理制度。",
      EN: "Taiwan SEHO builds its business foundation on integrity, professionalism, and responsibility, establishing a stable and sustainable corporate governance system.",
      JP: "台湾水禾は、誠実さ、専門性、責任を経営基盤とし、安定的で持続可能な企業統治制度を構築しています。",
    },

    items: {
      ZH: [
        "提供安全、高品質且符合衛生標準的產品與服務。",
        "所有營運活動兼顧食品安全、環境保護與職業安全。",
        "秉持公平、誠信與透明原則進行所有合作與交易。",
        "尊重人權與員工多樣性，建立安全、專業且具成長性的工作環境。",
        "主動揭露企業資訊，與客戶、員工及合作夥伴維持良好溝通。",
        "透過專業服務與社會參與，提升公共衛生與生活品質。",
        "建立完整風險管理與危機應變機制，因應天然災害、資訊安全與營運風險。",
        "經營團隊與管理階層以身作則，落實企業倫理與內部治理文化。",
      ],
      EN: [
        "Provide safe, high-quality products and services that meet hygiene standards.",
        "Ensure all operations consider food safety, environmental protection, and occupational safety.",
        "Conduct all partnerships and transactions with fairness, integrity, and transparency.",
        "Respect human rights and employee diversity while building a safe, professional, and growth-oriented workplace.",
        "Proactively disclose corporate information and maintain good communication with customers, employees, and partners.",
        "Improve public hygiene and quality of life through professional services and social participation.",
        "Establish comprehensive risk management and crisis response mechanisms for natural disasters, information security, and operational risks.",
        "Management teams lead by example and implement corporate ethics and internal governance culture.",
      ],
      JP: [
        "衛生基準を満たす安全で高品質な製品とサービスを提供する。",
        "すべての運営活動において、食品安全、環境保護、労働安全を考慮する。",
        "公平性、誠実性、透明性の原則に基づき、すべての協力と取引を行う。",
        "人権と従業員の多様性を尊重し、安全で専門的かつ成長性のある職場環境を構築する。",
        "企業情報を積極的に開示し、顧客、従業員、協力パートナーとの良好なコミュニケーションを維持する。",
        "専門サービスと社会参加を通じて、公共衛生と生活品質を向上させる。",
        "自然災害、情報セキュリティ、運営リスクに対応する包括的なリスク管理と危機対応体制を構築する。",
        "経営陣と管理層が率先して企業倫理と内部統治文化を実践する。",
      ],
    },
  },
};

function getLocalized(value, lang, fallback = "") {
  if (!value) return fallback;

  if (typeof value === "string") {
    return value;
  }

  return value[lang] || value.ZH || value.EN || value.JP || fallback;
}

function getLocalizedList(value, lang) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  return value[lang] || value.ZH || value.EN || value.JP || [];
}

function getFontFamily(fonts, lang) {
  if (typeof fonts === "string") {
    return fonts;
  }

  return (
    fonts?.[lang] || fonts?.ZH || '"LINE Seed TW","Noto Sans TC",system-ui'
  );
}

function useScrollSlideState(sectionRef, slideCount) {
  const [slideState, setSlideState] = useState({
    activeIndex: 0,
    previousIndex: null,
    scrollDirection: "down",
    transitionId: 0,
  });

  const activeIndexRef = useRef(0);
  const lastScrollYRef = useRef(0);
  const lastSectionTopRef = useRef(null);

  useEffect(() => {
    if (!slideCount) {
      return undefined;
    }

    let frameId = null;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const setActiveSlide = (nextIndex, nextDirection) => {
      const safeIndex = clamp(nextIndex, 0, slideCount - 1);

      if (safeIndex === activeIndexRef.current) {
        return;
      }

      const previousIndex = activeIndexRef.current;

      activeIndexRef.current = safeIndex;

      setSlideState((currentState) => ({
        activeIndex: safeIndex,
        previousIndex,
        scrollDirection: nextDirection,
        transitionId: currentState.transitionId + 1,
      }));
    };

    const updateActiveSlide = () => {
      frameId = null;

      const section = sectionRef.current;

      if (!section) {
        return;
      }

      const isDesktop = window.matchMedia("(min-width: 900px)").matches;

      if (!isDesktop) {
        setActiveSlide(0, "up");
        return;
      }

      const rect = section.getBoundingClientRect();

      const currentScrollY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;

      let nextDirection =
        currentScrollY >= lastScrollYRef.current ? "down" : "up";

      if (
        currentScrollY === lastScrollYRef.current &&
        lastSectionTopRef.current != null
      ) {
        nextDirection =
          rect.top <= lastSectionTopRef.current ? "down" : "up";
      }

      lastScrollYRef.current = currentScrollY;
      lastSectionTopRef.current = rect.top;

      const stickyTop = window.matchMedia("(min-width: 1200px)").matches
        ? 120
        : 105;

      const triggerLine = stickyTop + 220;

      const markers = Array.from(
        section.querySelectorAll("[data-esg-slide-marker]"),
      );

      let nextIndex = 0;

      if (markers.length) {
        markers.forEach((marker) => {
          const markerIndex = Number(marker.dataset.esgSlideMarker);

          if (!Number.isFinite(markerIndex)) {
            return;
          }

          const markerRect = marker.getBoundingClientRect();

          if (markerRect.top <= triggerLine) {
            nextIndex = markerIndex;
          }
        });
      } else {
        const slideStepFromDom = Number(section.dataset.esgSlideStep);

        const slideStep =
          Number.isFinite(slideStepFromDom) && slideStepFromDom > 0
            ? slideStepFromDom
            : 420;

        const distancePastStickyStart = Math.max(0, stickyTop - rect.top);

        nextIndex = Math.floor(distancePastStickyStart / slideStep);
      }

      setActiveSlide(nextIndex, nextDirection);
    };

    const requestUpdate = () => {
      if (frameId != null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateActiveSlide);
    };

    requestUpdate();

    window.addEventListener("scroll", requestUpdate, {
      passive: true,
    });

    window.addEventListener("resize", requestUpdate);

    document.addEventListener("scroll", requestUpdate, true);

    return () => {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      document.removeEventListener("scroll", requestUpdate, true);
    };
  }, [sectionRef, slideCount]);

  return slideState;
}

function CoreDirectionSection({ lang, fontFamily }) {
  const section = ESG_STRATEGY_DATA.coreSection;
  const slides = Array.isArray(section.slides) ? section.slides : [];

  const scrollAreaRef = useRef(null);

  const {
    activeIndex,
    previousIndex,
    scrollDirection,
    transitionId,
  } = useScrollSlideState(scrollAreaRef, slides.length);

  if (!slides.length) {
    return null;
  }

  const sectionTitle = getLocalized(section.title, lang);

  const activeSlide = slides[activeIndex] || slides[0];

  const previousSlide =
    previousIndex == null ? null : slides[previousIndex] || null;

  const previousTextExitAnimation =
    scrollDirection === "down"
      ? "esgStrategyTextExitToTop"
      : "esgStrategyTextExitToBottom";

  const activeTextEnterAnimation =
    scrollDirection === "down"
      ? "esgStrategyTextEnterFromBottom"
      : "esgStrategyTextEnterFromTop";

  const desktopSlideStep = 420;

  const desktopSlideHoldAfterLast = 520;

  const desktopPinnedExtraDistance =
    Math.max(slides.length - 1, 0) * desktopSlideStep +
    desktopSlideHoldAfterLast;

  const textTransitionDistance = 280;

  const textTransitionDuration = 950;

  const renderTextContent = (slide) => (
    <Box>
      <Typography
        sx={{
          fontFamily,
          fontWeight: 700,

          fontSize: {
            md: 30,
            lg: 30,
          },

          lineHeight: 1.25,

          color: MAIN_COLOR,

          mb: {
            md: 3,
            lg: 3.5,
          },
        }}
      >
        {getLocalized(slide.title, lang)}
      </Typography>

      <Box
        component="ul"
        sx={{
          m: 0,

          pl: {
            md: 3,
            lg: 3.25,
          },
        }}
      >
        {getLocalizedList(slide.bullets, lang).map((item, itemIndex) => (
          <Typography
            key={itemIndex}
            component="li"
            sx={{
              fontFamily,

              fontWeight: 400,

              fontSize: {
                md: 20,
                lg: 22,
              },

              lineHeight: 1.55,

              color: MAIN_COLOR,

              mb: {
                md: 2,
                lg: 2.25,
              },

              "&::marker": {
                color: ACCENT_COLOR,
                fontSize: "1.35em",
              },
            }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#ffffff",

        pt: {
          xs: 5,
          md: 6,
        },

        pb: {
          xs: 6,
          md: 8,
        },
      }}
    >
      <Typography
        sx={{
          fontFamily,
          fontWeight: 700,

          fontSize: {
            xs: 24,
            md: 35,
          },

          lineHeight: 1.3,

          color: MAIN_COLOR,

          textAlign: "center",

          mb: {
            xs: 4,
            md: 0,
          },
        }}
      >
        {sectionTitle}
      </Typography>

      {/* Mobile / tablet portrait: show the slides stacked. */}
      <Box
        sx={{
          display: {
            xs: "grid",
            md: "none",
          },

          gap: 4,

          maxWidth: 680,

          mx: "auto",

          px: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        {slides.map((slide) => (
          <Box
            key={slide.key}
            sx={{
              display: "grid",

              gap: 2.5,
            }}
          >
            <Box
              component="img"
              src={slide.image}
              alt={getLocalized(slide.title, lang)}
              loading="lazy"
              decoding="async"
              sx={{
                display: "block",

                width: "100%",

                aspectRatio: "1.08 / 1",

                objectFit: "cover",

                borderRadius: {
                  xs: "20px 96px 18px 18px",
                  sm: "22px 124px 20px 20px",
                },
              }}
            />

            <Box>
              <Typography
                sx={{
                  fontFamily,
                  fontWeight: 700,

                  fontSize: {
                    xs: 22,
                    sm: 24,
                  },

                  lineHeight: 1.25,

                  color: MAIN_COLOR,

                  mb: 2,
                }}
              >
                {getLocalized(slide.title, lang)}
              </Typography>

              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                }}
              >
                {getLocalizedList(slide.bullets, lang).map((item, index) => (
                  <Typography
                    key={index}
                    component="li"
                    sx={{
                      fontFamily,

                      fontWeight: 700,

                      fontSize: {
                        xs: 16,
                        sm: 17,
                      },

                      lineHeight: 1.65,

                      color: MAIN_COLOR,

                      mb: 1.25,

                      "&::marker": {
                        color: ACCENT_COLOR,
                        fontSize: "1.25em",
                      },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Desktop: stepped pinned section. Image fades; text exits/enters vertically. */}
      <Box
        ref={scrollAreaRef}
        data-esg-slide-step={desktopSlideStep}
        sx={{
          display: {
            xs: "none",
            md: "block",
          },

          position: "relative",

          /*
           * This is the real scroll-hold distance.
           * The visual block stays sticky while the markers change slide 1 → 2 → 3.
           */
          minHeight: {
            md: `calc(100svh + ${desktopPinnedExtraDistance}px)`,
            lg: `calc(100svh + ${desktopPinnedExtraDistance}px)`,
          },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",

            top: 0,
            left: 0,
            right: 0,

            opacity: 0,

            pointerEvents: "none",
          }}
        >
          {slides.map((slide, index) => (
            <Box
              key={`marker-${slide.key}`}
              data-esg-slide-marker={index}
              sx={{
                height:
                  index === slides.length - 1
                    ? desktopSlideHoldAfterLast
                    : desktopSlideStep,
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            position: "sticky",

            zIndex: 1,

            top: {
              md: 105,
              lg: 120,
            },

            maxWidth: 1400,

            mx: "auto",

            px: {
              md: 3,
              lg: 0,
            },

            minHeight: {
              md: "calc(100svh - 135px)",
              lg: "calc(100svh - 155px)",
            },

            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",

              display: "grid",

              gridTemplateColumns: {
                md: "minmax(520px, 1.08fr) minmax(0, 0.92fr)",
              },

              gap: {
                md: 6,
                lg: 7,
              },

              alignItems: "center",
            }}
          >
            {/* LEFT FIXED SHAPED IMAGE FRAME */}
            <Box
              sx={{
                position: "relative",

                width: "100%",

                maxWidth: {
                  md: 560,
                  lg: 650,
                },

                aspectRatio: "1.08 / 1",

                justifySelf: "center",

                overflow: "hidden",

                borderRadius: {
                  md: "22px 170px 22px 22px",
                  lg: "100px 200px 24px 100px",
                },

                boxShadow: "0 22px 52px rgba(0, 95, 145, 0.12)",

                bgcolor: "#eef4f7",
              }}
            >
              {slides.map((slide, index) => {
                const isActive = index === activeIndex;

                return (
                  <Box
                    key={slide.key}
                    component="img"
                    src={slide.image}
                    alt={getLocalized(slide.title, lang)}
                    loading={index === 0 ? "eager" : "lazy"}
                    decoding="async"
                    sx={{
                      position: "absolute",
                      inset: 0,

                      display: "block",

                      width: "100%",
                      height: "100%",

                      objectFit: "cover",

                      opacity: isActive ? 1 : 0,

                      transform: isActive ? "scale(1)" : "scale(1.035)",

                      transition:
                        "opacity 460ms ease, transform 620ms cubic-bezier(.2,.8,.2,1)",

                      willChange: "opacity, transform",

                      zIndex: isActive ? 2 : 1,
                    }}
                  />
                );
              })}
            </Box>

            {/* RIGHT TEXT TRANSITION STAGE */}
            <Box
              sx={{
                position: "relative",

                minHeight: {
                  md: 560,
                  lg: 540,
                },

                overflow: "visible",

                "@keyframes esgStrategyTextEnterFromBottom": {
                  "0%": {
                    opacity: 0,
                    transform: `translate3d(0, calc(-50% + ${textTransitionDistance}px), 0)`,
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translate3d(0, -50%, 0)",
                  },
                },

                "@keyframes esgStrategyTextEnterFromTop": {
                  "0%": {
                    opacity: 0,
                    transform: `translate3d(0, calc(-50% - ${textTransitionDistance}px), 0)`,
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translate3d(0, -50%, 0)",
                  },
                },

                "@keyframes esgStrategyTextExitToTop": {
                  "0%": {
                    opacity: 1,
                    transform: "translate3d(0, -50%, 0)",
                  },
                  "100%": {
                    opacity: 0,
                    transform: `translate3d(0, calc(-50% - ${textTransitionDistance}px), 0)`,
                  },
                },

                "@keyframes esgStrategyTextExitToBottom": {
                  "0%": {
                    opacity: 1,
                    transform: "translate3d(0, -50%, 0)",
                  },
                  "100%": {
                    opacity: 0,
                    transform: `translate3d(0, calc(-50% + ${textTransitionDistance}px), 0)`,
                  },
                },
              }}
            >
              {previousSlide ? (
                <Box
                  key={`previous-${previousSlide.key}-${transitionId}`}
                  sx={{
                    position: "absolute",

                    top: "50%",
                    left: 0,
                    right: 0,

                    transform: "translate3d(0, -50%, 0)",

                    animation: `${previousTextExitAnimation} ${textTransitionDuration}ms cubic-bezier(.16,.84,.24,1) both`,

                    pointerEvents: "none",

                    willChange: "opacity, transform",

                    zIndex: 1,
                  }}
                >
                  {renderTextContent(previousSlide)}
                </Box>
              ) : null}

              <Box
                key={`active-${activeSlide.key}-${transitionId}`}
                sx={{
                  position: "absolute",

                  top: "50%",
                  left: 0,
                  right: 0,

                  transform: "translate3d(0, -50%, 0)",

                  animation:
                    previousSlide == null
                      ? "none"
                      : `${activeTextEnterAnimation} ${textTransitionDuration}ms cubic-bezier(.16,.84,.24,1) both`,

                  pointerEvents: "auto",

                  willChange: "opacity, transform",

                  zIndex: 2,
                }}
              >
                {renderTextContent(activeSlide)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FutureCommitmentSection({ lang, fontFamily }) {
  const section = ESG_STRATEGY_DATA.futureSection;

  const milestones = Array.isArray(section.milestones)
    ? section.milestones
    : [];

  const defaultMilestoneKey = "";

  const [activeMilestoneKey, setActiveMilestoneKey] = useState(
    defaultMilestoneKey,
  );

  return (
    <Box
      component="section"
      sx={{
        width: "100vw",

        ml: "calc(50% - 50vw)",

        bgcolor: MAIN_COLOR,

        borderTopLeftRadius: {
          xs: "50% 44px",
          md: "50% 120px",
          lg: "95% 300px",
        },

        borderTopRightRadius: {
          xs: "50% 44px",
          md: "50% 120px",
          lg: "95% 300px",
        },

        overflow: "hidden",

        pt: {
          xs: 8,
          md: 10,
          lg: 12,
        },

        pb: {
          xs: 7,
          md: 6,
        },

        px: {
          xs: 2,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,

          mx: "auto",

          color: "#ffffff",

          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily,
            fontWeight: 700,

            fontSize: {
              xs: 26,
              md: 40,
            },

            lineHeight: 1.25,

            mb: {
              xs: 3,
              md: 2.75,
            },
          }}
        >
          {getLocalized(section.title, lang)}
        </Typography>

        <Typography
          sx={{
            maxWidth: 720,

            mx: "auto",

            fontFamily,
            fontWeight: 400,
            whiteSpace: "pre-line",
            textAlign: "center",

            fontSize: {
              xs: 14.5,
              md: 20,
            },

            lineHeight: {
              xs: 1.85,
              md: 1.85,
            },

            mb: {
              xs: 4,
              md: 3,
            },
          }}
        >
          {getLocalized(section.description, lang)}
        </Typography>

        <Box
          sx={{
            maxWidth: 1200,

            mx: "auto",

            bgcolor: "#ffffff",

            color: MAIN_COLOR,

            borderRadius: {
              xs: 5,
              md: 20,
            },

            px: {
              xs: 3,
              md: 5,
            },

            py: {
              xs: 3,
              md: 2.5,
              lg: 5
            },

            mb: {
              xs: 5,
              md: 9,
            },

            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              md: "210px minmax(0, 1fr)",
            },

            alignItems: "center",

            columnGap: {
              xs: 0,
              md: 4,
            },

            rowGap: {
              xs: 2,
              md: 0,
            },

            textAlign: {
              xs: "center",
              md: "left",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily,
              fontWeight: 700,

              fontSize: {
                xs: 22,
                md: 30,
              },

              lineHeight: 1.25,

              textAlign: {
                xs: "center",
                md: "center",
              },
            }}
          >
            {getLocalized(section.focusTitle, lang)}
          </Typography>

          <Box
            component="ol"
            sx={{
              m: 0,

              pl: {
                xs: 2.5,
                md: 10,
              },

              textAlign: "left",
            }}
          >
            {getLocalizedList(section.focusItems, lang).map((item, index) => (
              <Typography
                key={index}
                component="li"
                sx={{
                  fontFamily,
                  fontWeight: 700,

                  fontSize: {
                    xs: 13,
                    md: 20,
                  },

                  lineHeight: {
                    xs: 1.7,
                    md: 1.65,
                  },

                  color: MAIN_COLOR,

                  mb: index === getLocalizedList(section.focusItems, lang).length - 1
                    ? 0
                    : 1.7,

                  "&::marker": {
                    color: MAIN_COLOR,
                    fontWeight: 700,
                    textAlign: "left"
                  },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily,
            fontWeight: 700,

            fontSize: {
              xs: 24,
              md: 40,
            },

            lineHeight: 1.25,

            mb: {
              xs: 4,
              md: 4.5,
            },
          }}
        >
          {lang === "EN"
            ? "Sustainability Milestones"
            : lang === "JP"
              ? "サステナビリティ・マイルストーン"
              : "永續里程碑"}
        </Typography>

        <Box
          onMouseLeave={() => setActiveMilestoneKey(defaultMilestoneKey)}
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(3, minmax(0, 1fr))",
            },

            gap: {
              xs: 3,
              md: 3,
              lg: 3.5,
            },

            alignItems: "center",

            justifyItems: "center",
          }}
        >
          {milestones.map((milestone) => {
            const isActive = activeMilestoneKey === milestone.key;
            const milestoneItems = getLocalizedList(milestone.items, lang);

            return (
              <Box
                key={milestone.key}
                component="button"
                type="button"
                onMouseEnter={() => setActiveMilestoneKey(milestone.key)}
                onFocus={() => setActiveMilestoneKey(milestone.key)}
                onBlur={() => setActiveMilestoneKey(defaultMilestoneKey)}
                sx={{
                  width: {
                    xs: 250,
                    sm: 270,
                    md: 225,
                    lg: 450,
                  },

                  maxWidth: "100%",

                  aspectRatio: "1 / 1",

                  p: {
                    xs: 3,
                    md: 2.8,
                  },

                  border: "none",

                  borderRadius: 999,

                  bgcolor: isActive ? MILESTONE_HOVER_COLOR : "#ffffff",

                  color: MAIN_COLOR,

                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",

                  textAlign: "center",

                  cursor: "pointer",

                  appearance: "none",

                  boxShadow: "none",

                  transform: isActive ? "translateY(-3px)" : "translateY(0)",

                  transition:
                    "background-color 180ms ease, transform 180ms cubic-bezier(.2,.8,.2,1)",

                  "&:focus-visible": {
                    outline: "3px solid rgba(255,255,255,0.9)",
                    outlineOffset: 5,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily,
                    fontWeight: 700,

                    fontSize: {
                      xs: 22,
                      md: 21,
                      lg: 35,
                    },

                    lineHeight: 1.25,

                    mb: isActive
                      ? {
                          xs: 1.6,
                          md: 1.5,
                        }
                      : 0,
                  }}
                >
                  {getLocalized(milestone.title, lang)}
                </Typography>

                {isActive ? (
                  <Box
                    component="ul"
                    sx={{
                      m: 0,

                      pl: {
                        xs: 2,
                        md: 4,
                      },

                      textAlign: "left",
                    }}
                  >
                    {milestoneItems.map((item, index) => (
                      <Typography
                        key={index}
                        component="li"
                        sx={{
                          fontFamily,
                          fontWeight: 400,

                          fontSize: {
                            xs: 12.5,
                            md: 11.5,
                            lg: 20,
                          },

                          lineHeight: {
                            xs: 1.5,
                            md: 1.45,
                          },

                          color: MAIN_COLOR,

                          mb: index === milestoneItems.length - 1 ? 0 : 0.45,

                          "&::marker": {
                            color: ACCENT_COLOR,
                            fontSize: "1.25em",
                          },
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function GovernancePrincipleSection({ lang, fontFamily }) {
  const section = ESG_STRATEGY_DATA.governanceSection;

  return (
    <Box
      component="section"
      sx={{
        width: "100vw",

        ml: "calc(50% - 50vw)",

        bgcolor: MAIN_COLOR,

        pt: {
          xs: 2,
          md: 3,
        },

        pb: {
          xs: 7,
          md: 9,
        },

        px: {
          xs: 2,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1120,

          mx: "auto",

          color: "#ffffff",

          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily,
            fontWeight: 700,

            fontSize: {
              xs: 26,
              md: 40,
            },

            lineHeight: 1.25,

            mb: {
              xs: 2.5,
              md: 3.5,
            },
          }}
        >
          {getLocalized(section.title, lang)}
        </Typography>

        <Typography
          sx={{
            maxWidth: 940,

            mx: "auto",

            fontFamily,
            fontWeight: 400,

            fontSize: {
              xs: 15,
              md: 19,
              lg: 23,
            },

            lineHeight: {
              xs: 1.85,
              md: 1.9,
            },

            mb: {
              xs: 4,
              md: 5,
            },
          }}
        >
          {getLocalized(section.description, lang)}
        </Typography>

        <Box
          sx={{
            maxWidth: 700,

            mx: "auto",

            textAlign: "left",
          }}
        >
          <Box
            component="ul"
            sx={{
              m: 0,

              pl: {
                xs: 2.5,
                md: 3,
              },
            }}
          >
            {getLocalizedList(section.items, lang).map((item, index) => (
              <Typography
                key={index}
                component="li"
                sx={{
                  fontFamily,
                  fontWeight: 400,

                  fontSize: {
                    xs: 15,
                    md: 20,
                  },

                  lineHeight: {
                    xs: 1.8,
                    md: 1.9,
                  },

                  color: "#ffffff",

                  mb: {
                    xs: 1,
                    md: 1.25,
                  },

                  "&::marker": {
                    color: getPrimaryColor("b2b"),
                  },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function EsgStrategy({ lang = "ZH", fonts }) {
  const fontFamily = getFontFamily(fonts, lang);

  const sectionTitle = getLocalized(ESG_STRATEGY_DATA.sectionTitle, lang);

  const subtitle = getLocalized(ESG_STRATEGY_DATA.subtitle, lang);

  const intro = getLocalized(ESG_STRATEGY_DATA.intro, lang);

  return (
    <Box
      component="section"
      sx={{
        width: "100%",

        bgcolor: "#ffffff",
      }}
    >
      {/* TOP WHITE INTRO CONTENT */}
      <Box
        sx={{
          pt: {
            xs: 3,
            md: 0,
            lg: 0,
          },

          pb: {
            xs: 4,
            md: 5,
          },

          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily,

            fontWeight: 700,

            fontSize: {
              xs: 24,
              md: 40,
            },

            lineHeight: 1.35,

            color: getPrimaryColor("b2b"),

            mb: {
              xs: 3,
              md: 4,
            },
          }}
        >
          {sectionTitle}
        </Typography>

        <Box
          sx={{
            maxWidth: {
              xs: "100%",
              md: 1100,
            },

            mx: "auto",

            px: {
              xs: 2,
              md: 3,
            },

            textAlign: "center",
          }}
        >
          {/* <Typography
            sx={{
              fontFamily,

              fontWeight: 700,

              fontSize: {
                xs: 17,
                md: 30,
              },

              lineHeight: 1.55,

              color: getPrimaryColor("b2b"),

              mb: {
                xs: 1.5,
                md: 2,
              },
            }}
          >
            {subtitle}
          </Typography> */}

          <Typography
            sx={{
              fontFamily,

              fontWeight: 400,

              fontSize: {
                xs: 14,
                md: 20,
              },

              lineHeight: {
                xs: 1.85,
                md: 1.9,
              },

              color: getPrimaryColor("b2b"),
            }}
          >
            {intro}
          </Typography>
        </Box>
      </Box>

      <CoreDirectionSection lang={lang} fontFamily={fontFamily} />

      <FutureCommitmentSection lang={lang} fontFamily={fontFamily} />

      <GovernancePrincipleSection lang={lang} fontFamily={fontFamily} />
    </Box>
  );
}
